import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getLogEntry } from '@/features/elva-work/store';
import type { HitlItem } from '../types';
import { answerCredit, remindCreditLater } from '../store';
import type { CreditChoice } from '../store';
import { CA_NOTICE_DAYS, CA_RULE_TEXT, CA_RULE_TITLE, REMIND_CUTOFF_DAYS } from '../credit';
import { fmtMonthDay, fmtMonthDayYear } from '../dates';
import { Spinner } from './Detail';
import { PMS } from '@/features/shared/pms';

interface Props {
  item: HitlItem;
  onBack?: () => void;
}

/**
 * Patient credit (D198, H8 §3). A person always decides this one, so there is no
 * "I can't do this", and there is no refund, transfer or apply control: ELVA only
 * checks the PMS and writes a note.
 */
export function CreditDetail({ item, onBack }: Props) {
  const credit = item.credit;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const doneRef = useRef<HTMLHeadingElement>(null);
  const [phase, setPhase] = useState<'form' | 'working'>('form');
  const [workLine, setWorkLine] = useState('');
  const [choice, setChoice] = useState<CreditChoice | ''>('');
  const [toldIso, setToldIso] = useState('');
  const [askedNote, setAskedNote] = useState('');
  const [ruleOpen, setRuleOpen] = useState(false);

  const done = item.removed === 'done';

  useEffect(() => {
    headingRef.current?.focus();
    setPhase('form'); setWorkLine(''); setChoice(''); setToldIso(''); setAskedNote(''); setRuleOpen(false);
  }, [item.id]);
  useEffect(() => { if (done) doneRef.current?.focus(); }, [done]);

  if (!credit) return null;

  const first = credit.patientFirst;
  const he = credit.possessive === 'his' ? 'he' : 'she';
  const California = credit.tellByIso !== undefined;

  const header = (
    <div>
      <span className="hl-eyebrow">{item.kindLabel}</span>
      <h2 className="hl-title" tabIndex={-1} ref={headingRef}>{item.title}</h2>
      <p className="hl-meta">{item.metaLine}</p>
    </div>
  );
  const back = onBack ? <button type="button" className="hl-back" onClick={onBack}>← All items</button> : null;
  const history = <p className="hl-history">History: {item.history.join(' · ')}</p>;

  // ── Closed ──
  if (done) {
    return (
      <main className="hl-detail hl-credit">
        {back}
        <div className="hl-detail-inner">
          {header}
          <section className="hl-done">
            <h3 className="hl-done-title" tabIndex={-1} ref={doneRef}><span className="hl-tick">✓</span> Done</h3>
            {item.doneFindings?.map((f, i) => (
              <div key={`f-${String(i)}`} className="hl-tickrow"><span className="hl-tick">✓</span><div>{f}</div></div>
            ))}
            <p className="hl-meta" style={{ marginTop: 10 }}>{item.doneLabel ?? ''} Moved to Done in the last 30 days.</p>
          </section>
          {history}
        </div>
      </main>
    );
  }

  // ── Waiting to come back (told the patient / remind me) ──
  if (credit.snoozedUntil) {
    return (
      <main className="hl-detail hl-credit">
        {back}
        <div className="hl-detail-inner">
          {header}
          <section className="hl-credit-wait" role="status">
            <h3 className="hl-h3" style={{ fontSize: 18, margin: '0 0 6px' }} tabIndex={-1} ref={doneRef}>Saved</h3>
            <p style={{ margin: 0 }}>{credit.snoozeNote}</p>
          </section>
          {history}
        </div>
      </main>
    );
  }

  // ── Form ──
  const toldValid = toldIso !== '' && toldIso <= credit.asOfIso;
  const noteValid = askedNote.trim() !== '';
  const valid = choice === 'refunded' || choice === 'used' || (choice === 'told' && toldValid) || (choice === 'keep' && noteValid);

  let reason = 'Choose what happened first.';
  if (choice === 'told') reason = toldIso !== '' && !toldValid ? 'The date can’t be after today.' : `Enter the date we told ${credit.objective} first.`;
  if (choice === 'keep') reason = `Say when and how ${first} asked first.`;

  const daysLeft = credit.daysLeft;
  const lastDays = daysLeft !== undefined && daysLeft <= REMIND_CUTOFF_DAYS;
  const deadlineLine =
    daysLeft === undefined ? '' : daysLeft < 0 ? `The deadline passed ${String(-daysLeft)} ${-daysLeft === 1 ? 'day' : 'days'} ago.` : daysLeft === 0 ? `Today is the last day to tell ${first}.` : `${String(daysLeft)} ${daysLeft === 1 ? 'day' : 'days'} left to tell ${first}.`;

  const save = async (): Promise<void> => {
    if (choice === '' || !valid) return;
    setPhase('working');
    for await (const ev of answerCredit(item.id, { choice, ...(choice === 'told' ? { toldIso } : {}), ...(choice === 'keep' ? { askedNote } : {}) })) {
      if ('working' in ev) setWorkLine(ev.working);
    }
    setPhase('form');
  };

  const options: { value: CreditChoice; label: string; desc: string }[] = [
    { value: 'refunded', label: 'We refunded it', desc: `ELVA checks ${PMS} for the refund and closes this.` },
    { value: 'told', label: `We told ${first}; the refund is on its way`, desc: `ELVA checks for the refund and comes back here 30 days after the date we told ${credit.objective} if it isn’t in ${PMS}.` },
    { value: 'keep', label: `${first} asked to keep it as credit`, desc: `ELVA notes it on ${credit.possessive} account and stops reminding.` },
    { value: 'used', label: 'We used it on another visit', desc: `ELVA checks ${PMS} and closes this.` },
  ];

  return (
    <main className="hl-detail hl-credit">
      {back}
      <div className="hl-detail-inner">
        {header}

        <section className="hl-card"><p style={{ margin: 0, color: 'var(--fg2)' }}>{item.why}</p></section>

        {California && credit.tellByIso ? (
          <section className="hl-warn" aria-labelledby="hl-warn-title">
            <div className="hl-warn-hd"><h3 className="hl-h3" id="hl-warn-title">California: tell {first} by {fmtMonthDayYear(credit.tellByIso)}</h3></div>
            <div className="hl-warn-body">
              <p>California requires the practice to tell a patient about a payment like this within {String(CA_NOTICE_DAYS)} days of receiving it, then refund it within 30 days, unless the patient asks to keep it as credit. If the patient asks for a refund, it’s due within 30 days of asking.</p>
              {lastDays ? <p style={{ fontWeight: 600, color: 'var(--warning)' }}>{deadlineLine}</p> : null}
              <p className="hl-meta">
                {String(CA_NOTICE_DAYS)} days from {fmtMonthDay(credit.postedIso)} · {CA_RULE_TITLE} ·{' '}
                <button type="button" className="hl-src" aria-expanded={ruleOpen} aria-controls="hl-rule" onClick={() => { setRuleOpen((o) => !o); }}>Read the rule</button>
              </p>
              {ruleOpen ? <div id="hl-rule" className="hl-rule" role="region" aria-label={CA_RULE_TITLE}>{CA_RULE_TEXT}</div> : null}
            </div>
          </section>
        ) : credit.ruleLine ? (
          <p className="hl-meta">{credit.ruleLine}</p>
        ) : null}

        <section className="hl-card">
          <h3 className="hl-h3">What ELVA did first</h3>
          {item.tries.map((t, i) => (
            <div key={`try-${String(i)}`} className="hl-try">
              <span className="tt">{t.time}</span>
              <span className="ic ok">✓</span>
              <span>{t.result}</span>
              {getLogEntry(t.logEntryId) ? <Link to={`/elva-work/log/${t.logEntryId}`} className="hl-src">Log</Link> : <span />}
            </div>
          ))}
        </section>

        <section className="hl-card">
          <h3 className="hl-h3" id="hl-credit-q">What happened with {first}’s credit?</h3>
          <div className="hl-radio-list" role="radiogroup" aria-labelledby="hl-credit-q">
            {options.map((o) => (
              <div key={o.value}>
                <label className="hl-credit-opt">
                  <input type="radio" name="hl-credit" checked={choice === o.value} onChange={() => { setChoice(o.value); }} />
                  <span>
                    <strong>{o.label}</strong>
                    <div className="hl-credit-desc">{o.desc}</div>
                  </span>
                </label>
                {choice === o.value && o.value === 'told' ? (
                  <div className="hl-credit-follow">
                    <label className="hl-label" htmlFor="hl-credit-told">Date we told {credit.objective}</label>
                    <input id="hl-credit-told" className="hl-field" type="date" max={credit.asOfIso} value={toldIso} onChange={(e) => { setToldIso(e.target.value); }} />
                  </div>
                ) : null}
                {choice === o.value && o.value === 'keep' ? (
                  <div className="hl-credit-follow">
                    <label className="hl-label" htmlFor="hl-credit-asked">When and how {he} asked</label>
                    <input id="hl-credit-asked" className="hl-field" placeholder={`e.g. ${fmtMonthDay(credit.asOfIso)}, by phone`} value={askedNote} onChange={(e) => { setAskedNote(e.target.value); }} />
                    <p className="hl-meta" style={{ marginTop: 6 }}>ELVA writes this as a dated note on {credit.possessive} account in {PMS}.</p>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        {phase === 'working' ? (
          <div className="hl-live" aria-live="polite"><Spinner /><span>{workLine || `ELVA is checking ${PMS}…`}</span></div>
        ) : (
          <div className="hl-actionbar">
            <button type="button" className="hl-btn hl-btn-primary hl-btn-lg" disabled={!valid} aria-describedby={valid ? undefined : 'hl-credit-reason'} onClick={() => { void save(); }}>{item.savePrimary}</button>
            {credit.remindOffered ? (
              <button type="button" className="hl-btn hl-btn-ghost" onClick={() => { remindCreditLater(item.id); }}>Remind me in 2 weeks</button>
            ) : null}
            <span id="hl-credit-reason" className="hl-actionbar-meta">
              {valid ? `ELVA checks ${PMS}, then closes this or brings it back.` : reason}
              {!credit.remindOffered ? ` “Remind me” isn’t offered in the last ${String(REMIND_CUTOFF_DAYS)} days before the deadline.` : ''}
            </span>
          </div>
        )}

        {history}
      </div>
    </main>
  );
}
